// pfs-date-badge.ts — Kalender-Kachel für ein Datum (PFS-729, Termin-buchen-Redesign).
//
// Die kompakte „Datumsanzeige-Card" (Kalenderblatt-Optik): farbiges Monatsband
// oben, große Tageszahl in der Mitte, Wochentag unten. Löst das über die Cut-Apps
// verstreute hand-gerollte „when"-Textmuster (fmtDM/fmtHM) für Termin-/Slot-Listen
// ab — ein Datum wird plattformweit gleich präsentiert.
//
// Reiner Renderer (feedback_seams_not_flags): das WC parst nur das Datum und
// formatiert per Intl. WELCHES Datum, welche Farbe, ob „vergangen" — das reicht
// der Consumer über data-* rein (die „warum"-Logik bleibt in der Domäne).
//
// Locale: data-locale (z. B. "de-DE"); fehlt es, folgt es dem Dokument
// (`<html lang>`), sonst dem Runtime-Default. Kein hartkodiertes Tenant-Locale
// (pfs-components ist tenant-agnostisch).
//
// Attribute (alle data-*):
//   data-date     ISO-Datum/-Zeit (RFC3339 oder YYYY-MM-DD) → Monat/Tag/Wochentag
//   data-locale   BCP-47-Locale für Monat/Wochentag (Default: <html lang> / Runtime)
//   data-size     sm | md (Default) | lg
//   data-accent   Farbe des Monatsbands (Naht; Default --pfs-color-primary)
//   data-tone     default | muted (vergangenes Datum ausgegraut, wie avatar-offline)
//   data-weekday  show (Default) | hide (Wochentag-Zeile weglassen)
//
// CSS-Custom-Property-Nähte:
//   --pfs-date-badge-size    freie Kachelgröße (überschreibt data-size)
//   --pfs-date-badge-accent  Monatsband-Farbe (überschreibt data-accent)
//
// Beispiel:
//   <pfs-date-badge data-date="2026-07-21" data-locale="de-DE"></pfs-date-badge>
//   <pfs-date-badge data-date="2026-01-04" data-tone="muted" data-size="sm"></pfs-date-badge>
import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";

/* Größe als Naht: data-size gibt die Stufen-Defaults, --pfs-date-badge-size
   überschreibt frei. Das Monatsband nimmt data-accent bzw. den CSS-Prop. */
const STYLES = `
:host { display: inline-block; --_w: var(--pfs-date-badge-size, 3.25rem); }
:host([data-size="sm"]) { --_w: var(--pfs-date-badge-size, 2.6rem); }
:host([data-size="lg"]) { --_w: var(--pfs-date-badge-size, 4.25rem); }
.tile {
  box-sizing: border-box;
  width: var(--_w);
  border: 1px solid var(--pfs-color-outline);
  border-radius: var(--pfs-radius-md, 0.5rem);
  background: var(--pfs-color-surface);
  overflow: hidden;
  font-family: var(--pfs-font-family, inherit);
  text-align: center;
  line-height: 1;
  user-select: none;
}
.tile.muted { filter: grayscale(0.65); opacity: 0.6; }
.month {
  background: var(--pfs-date-badge-accent, var(--pfs-color-primary));
  color: var(--pfs-color-on-primary);
  font-size: calc(var(--_w) * 0.2);
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: calc(var(--_w) * 0.08) 0;
}
.day {
  color: var(--pfs-color-on-surface);
  font-size: calc(var(--_w) * 0.44);
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  padding: calc(var(--_w) * 0.09) 0 calc(var(--_w) * 0.05);
}
.weekday {
  color: var(--pfs-color-on-surface-muted);
  font-size: calc(var(--_w) * 0.17);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding-bottom: calc(var(--_w) * 0.1);
}
.invalid {
  color: var(--pfs-color-on-surface-muted);
  font-size: calc(var(--_w) * 0.44);
  font-weight: 700;
  padding: calc(var(--_w) * 0.22) 0;
}
`;

// Locale-Auflösung: data-locale > <html lang> > Runtime-Default (undefined).
function resolveLocale(host: HTMLElement): string | undefined {
  const explicit = host.dataset.locale;
  if (explicit) return explicit;
  const docLang = host.ownerDocument?.documentElement?.getAttribute("lang");
  return docLang || undefined;
}

// parseDate — robustes Parsen; ungültig → null (rendert "?").
function parseDate(raw: string | undefined): Date | null {
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

defineComponent({
  tagName: "pfs-date-badge",
  mixins: [withShadow()],
  render(host, ctx) {
    if (!ctx.shadowRoot) return;
    const root = ctx.shadowRoot;
    const style = document.createElement("style");
    style.textContent = STYLES;
    root.appendChild(style);

    const tile = document.createElement("div");
    tile.className = "tile";
    root.appendChild(tile);

    const paint = (): void => {
      tile.className = "tile";
      tile.textContent = "";
      if (host.dataset.tone === "muted") tile.classList.add("muted");

      const date = parseDate(host.dataset.date);
      if (!date) {
        const q = document.createElement("div");
        q.className = "invalid";
        q.textContent = "–";
        tile.appendChild(q);
        return;
      }
      const locale = resolveLocale(host);

      const month = document.createElement("div");
      month.className = "month";
      month.textContent = new Intl.DateTimeFormat(locale, { month: "short" })
        .format(date)
        .replace(/\.$/, "");
      tile.appendChild(month);

      const day = document.createElement("div");
      day.className = "day";
      day.textContent = String(date.getDate());
      tile.appendChild(day);

      if (host.dataset.weekday !== "hide") {
        const wd = document.createElement("div");
        wd.className = "weekday";
        wd.textContent = new Intl.DateTimeFormat(locale, { weekday: "short" })
          .format(date)
          .replace(/\.$/, "");
        tile.appendChild(wd);
      }
    };
    paint();
    const obs = new MutationObserver(paint);
    ctx.cleanup.push(() => obs.disconnect());
    obs.observe(host, {
      attributes: true,
      attributeFilter: ["data-date", "data-locale", "data-tone", "data-weekday"],
    });
  },
});
